import React, { useState, useEffect } from 'react';
import { useAuth } from "../context/AuthContext";
import { Calendar, Clock, Shield, Link2, RefreshCw, Trash2, Check, X } from 'lucide-react';
import supabase from "../lib/supabaseClient";

// Logo Components
const GoogleLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const OutlookLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path fill="#0078D4" d="M7 4h10c.55 0 1 .45 1 1v2h3c.55 0 1 .45 1 1v10c0 .55-.45 1-1 1h-3v2c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1V5c0-.55.45-1 1-1z"/>
    <path fill="#FFF" d="M12 9c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm0 5c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"/>
  </svg>
);


const CalendarSettings = () => {
  // Mock connection status - will be replaced with actual API calls
  const [googleConnected, setGoogleConnected] = useState(false);
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const { user } = useAuth();
  const tenantId = user?.tenant_id;
  // Availability mode
  const [availabilityMode, setAvailabilityMode] = useState('custom');
  const [waitingForOAuth, setWaitingForOAuth] = useState(false);

  // Custom availability windows
  const [customAvailability, setCustomAvailability] = useState({
    monday: { enabled: true, start: '09:00', end: '17:00' },
    tuesday: { enabled: true, start: '09:00', end: '17:00' },
    wednesday: { enabled: true, start: '09:00', end: '17:00' },
    thursday: { enabled: true, start: '09:00', end: '17:00' },
    friday: { enabled: true, start: '09:00', end: '17:00' },
    saturday: { enabled: false, start: '09:00', end: '17:00' },
    sunday: { enabled: false, start: '09:00', end: '17:00' }
  });

  // Call settings
  const [callDuration, setCallDuration] = useState(30);
  const [bufferTime, setBufferTime] = useState(15);

  // Scheduling behavior
  const [enforceBusinessHours, setEnforceBusinessHours] = useState(true);
  const [minimumNotice, setMinimumNotice] = useState(2);

  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

// Effect 1: OAuth callback detection - runs once when tenantId available
// Effect 1: OAuth callback detection AND session refresh
useEffect(() => {
  if (!tenantId) return;
  
  const search = window.location.search.substring(1);
  const params = new URLSearchParams(search);
  const isOAuthCallback = params.get('calendar_connected') === '1';
  const provider = params.get('provider');

  console.log('🔍 OAuth callback check:', { search, isOAuthCallback, provider, tenantId });

  if (isOAuthCallback && provider) {
    console.log(`📥 OAuth callback detected for ${provider}`);
    // Clean URL — no manual refreshSession() needed; autoRefreshToken handles JWT refresh.
    window.history.replaceState({}, document.title, window.location.pathname);
  }
}, [tenantId]); // Runs when tenantId becomes available

// Effect 2: Load settings - runs on tenantId change
useEffect(() => {
  if (!tenantId) return;
  
  const loadSettings = async (retryCount = 0) => {
    const { data, error } = await supabase
      .from("calendar_settings")
      .select("*")
      .eq("tenant_id", tenantId)
      .single();

    if (error || !data) {
      console.log("No saved calendar settings — creating default row.");

      const { error: insertError } = await supabase
        .from("calendar_settings")
        .insert({
          tenant_id: tenantId,
          availability_mode: "custom",
          custom_availability: customAvailability,
          call_duration: 30,
          buffer_time: 15,
          enforce_business_hours: true,
          minimum_notice: 2,
          google_connected: false,
          outlook_connected: false,
        });

      if (insertError) {
        console.error("Failed to create default calendar settings:", insertError);
        return;
      }

      return;
    }

    // Check if OAuth callback and retry if needed
    const search = window.location.search.substring(1);
    const params = new URLSearchParams(search);
    const isOAuthCallback = params.get('calendar_connected') === '1';
    const provider = params.get('provider');

    if (isOAuthCallback && retryCount === 0) {
      const isConnected = provider === "google" ? data.google_connected : data.outlook_connected;
      
      if (!isConnected) {
        console.log("⏳ OAuth redirect detected but database not updated yet, retrying...");
        setTimeout(() => loadSettings(1), 500);
        return;
      }
    }

    // Load values into state
    setAvailabilityMode(data.availability_mode || "custom");
    setCustomAvailability(
      data.custom_availability && typeof data.custom_availability === "object"
        ? data.custom_availability
        : {
            monday: { enabled: true, start: "09:00", end: "17:00" },
            tuesday: { enabled: true, start: "09:00", end: "17:00" },
            wednesday: { enabled: true, start: "09:00", end: "17:00" },
            thursday: { enabled: true, start: "09:00", end: "17:00" },
            friday: { enabled: true, start: "09:00", end: "17:00" },
            saturday: { enabled: false, start: "09:00", end: "17:00" },
            sunday: { enabled: false, start: "09:00", end: "17:00" }
          }
    );

    setCallDuration(data.call_duration || 30);
    setBufferTime(data.buffer_time || 15);
    setEnforceBusinessHours(data.enforce_business_hours ?? true);
    setMinimumNotice(data.minimum_notice ?? 2);
    setGoogleConnected(data.google_connected ?? false);
    setOutlookConnected(data.outlook_connected ?? false);
    setLastSync(data.last_sync || null);
    setHasChanges(false);

    console.log("✅ Loaded calendar settings:", {
      google: data.google_connected,
      outlook: data.outlook_connected
    });
  };

  loadSettings();
}, [tenantId]);

  const handleConnectGoogle = async () => {
  if (!tenantId) return alert("Missing tenant ID");

  try {
    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/calendar-oauth`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: "get_auth_url",
          tenant_id: tenantId,
          provider: "google",
          redirect_to: `${window.location.origin}/oauth-callback`,
        }),
      }
    );

    const json = await response.json();
    
    if (!json.success || !json.auth_url) {
      console.error("OAuth failed:", json);
      alert("Could not start Google OAuth.");
      return;
    }

    setWaitingForOAuth(true);  // ADD THIS

    // Open in popup
    const popup = window.open(
      json.auth_url,
      'GoogleOAuth',
      'width=600,height=700,left=200,top=100'
    );

    // Listen for message from popup
    // Inside handleConnectGoogle (and same for handleConnectOutlook)

const messageHandler = async (event) => {
  if (event.origin !== window.location.origin) return;
  
  if (event.data.type === 'OAUTH_SUCCESS' && event.data.provider === 'google') {
    console.log('✅ Google calendar connected via popup');
    
    // Close popup
    if (popup && !popup.closed) {
      popup.close();
    }
    
    // Clear the interval
    clearInterval(checkInterval);
    
    // Reload settings to show connection
    window.location.reload();
    
    // Clean up listener
    window.removeEventListener('message', messageHandler);
  }
};

window.addEventListener('message', messageHandler);

// UPDATED: Don't hide banner when popup closes - let it stay for manual refresh
const checkInterval = setInterval(() => {
  if (popup.closed) {
    clearInterval(checkInterval);
    // REMOVED: setWaitingForOAuth(false);  // Don't hide the banner
    window.removeEventListener('message', messageHandler);
  }
}, 500);
    
  } catch (err) {
    console.error("Google OAuth error:", err);
    alert("Could not start Google OAuth");
    setWaitingForOAuth(false);
  }
};

const handleConnectOutlook = async () => {
  if (!tenantId) return alert("Missing tenant ID");

  try {
    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/calendar-oauth`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: "get_auth_url",
          tenant_id: tenantId,
          provider: "microsoft",
          redirect_to: `${window.location.origin}/oauth-callback`,
        }),
      }
    );

    const json = await response.json();
    
    if (!json.success || !json.auth_url) {
      console.error("OAuth failed:", json);
      alert("Could not start Microsoft OAuth.");
      return;
    }

    setWaitingForOAuth(true);

    // Open in popup
    const popup = window.open(
      json.auth_url,
      'MicrosoftOAuth',
      'width=600,height=700,left=200,top=100'
    );

    // Listen for message from popup
    const messageHandler = async (event) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data.type === 'OAUTH_SUCCESS' && event.data.provider === 'microsoft') {
        console.log('✅ Microsoft calendar connected via popup');
        
        // Close popup
        if (popup && !popup.closed) {
          popup.close();
        }
        
        // Reload settings to show connection
        window.location.reload();
        
        // Clean up listener
        window.removeEventListener('message', messageHandler);
      }
    };

    window.addEventListener('message', messageHandler);

    // Auto-close timer - if popup closes without success message, reset state
    const checkInterval = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkInterval);
        setWaitingForOAuth(false);
        window.removeEventListener('message', messageHandler);
      }
    }, 500);
    
  } catch (err) {
    console.error("Microsoft OAuth error:", err);
    alert("Could not start Microsoft OAuth");
    setWaitingForOAuth(false);
  }
};

  const handleDisconnect = async (provider) => {
  if (!window.confirm(`Disconnect ${provider} calendar?`)) return;

  try {
    // Delete from calendar_connections table
    const { error: deleteError } = await supabase
      .from("calendar_connections")
      .delete()
      .eq("tenant_id", tenantId)
      .eq("provider", provider);

    if (deleteError) throw deleteError;

    // Update calendar_settings
    const { error: updateError } = await supabase
      .from("calendar_settings")
      .update(
        provider === "google"
          ? { google_connected: false, last_sync: null }
          : { outlook_connected: false, last_sync: null }
      )
      .eq("tenant_id", tenantId);

    if (updateError) throw updateError;

    // Update UI immediately - NO hasChanges flag
    if (provider === "google") {
      setGoogleConnected(false);
    } else {
      setOutlookConnected(false);
    }
    setLastSync(null);

  } catch (error) {
    console.error("Failed to disconnect:", error);
    alert("Error disconnecting calendar");
  }
};

  const handleSync = async (provider) => {
  if (!tenantId) return;

  try {
    console.log(`🔄 Syncing ${provider} calendar...`);
    
    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/calendar-oauth`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          action: "sync_calendar",
          tenant_id: tenantId,
          provider: provider,
        }),
      }
    );

    const json = await response.json();
    
    if (json.success) {
      const syncTime = new Date().toLocaleString();
      setLastSync(syncTime);
      
      // Update database with last_sync time
      await supabase
        .from("calendar_settings")
        .update({ last_sync: new Date().toISOString() })
        .eq("tenant_id", tenantId);
      
      console.log(`✅ Synced ${json.events_count || 0} events from ${provider}`);
      alert(`Successfully synced ${json.events_count || 0} calendar events`);
    } else {
      console.error("Sync failed:", json);
      alert("Sync failed: " + (json.error || "Unknown error"));
    }
  } catch (err) {
    console.error("Sync error:", err);
    alert("Could not sync calendar");
  }
};

  const updateCustomAvailability = (day, field, value) => {
    setCustomAvailability(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
    setHasChanges(true);
  };

  const handleCancel = () => {
    window.location.reload();
  };

  const handleSave = async () => {
    setSaving(true);

    const { error } = await supabase
      .from("calendar_settings")
      .upsert(
        {
          tenant_id: tenantId,
          availability_mode: availabilityMode,
          custom_availability: customAvailability,
          call_duration: callDuration,
          buffer_time: bufferTime,
          enforce_business_hours: enforceBusinessHours,
          minimum_notice: minimumNotice,
          google_connected: googleConnected,
          outlook_connected: outlookConnected,
          last_sync: lastSync,
          updated_at: new Date(),
        },
        { onConflict: "tenant_id" }
      );

    if (error) {
      console.error(error);
      alert("Error saving settings.");
    } else {
      setHasChanges(false);
    }

    setSaving(false);
  };


  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="max-w-4xl mx-auto p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Calendar Settings</h1>
          <p className="text-gray-600">
            Configure how SurFox AI schedules calls and references your availability.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Link2 className="w-5 h-5 text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-900">Calendar Integration</h2>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Connect your calendar to allow SurFox AI to reference your real-time availability when scheduling calls.
          </p>

          {/* Google Calendar */}
          <div className="border border-gray-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                  <GoogleLogo />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Google Calendar</h3>
                  <p className="text-sm text-gray-500">
                    {googleConnected ? 'Connected' : 'Not connected'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {googleConnected ? (
                  <>
                    <button
                      onClick={() => handleSync('google')}
                      className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center gap-1"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Sync
                    </button>
                    <button
                      onClick={() => handleDisconnect('google')}
                      className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md flex items-center gap-1"
                    >
                      <Trash2 className="w-4 h-4" />
                      Remove
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleConnectGoogle}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
                  >
                    Connect
                  </button>
                )}
              </div>
            </div>
            {googleConnected && lastSync && (
              <p className="text-xs text-gray-500 mt-2">Last synced: {lastSync}</p>
            )}
          </div>

          {/* Outlook Calendar */}
<div className="border border-gray-200 rounded-lg p-4">
  <div className="flex items-center justify-between mb-2">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200">
        <OutlookLogo />
      </div>
      <div>
        <h3 className="font-semibold text-gray-900">Microsoft / Outlook Calendar</h3>
        <p className="text-sm text-gray-500">
          {outlookConnected ? 'Connected' : 'Not connected'}
        </p>
      </div>
    </div>
    <div className="flex items-center gap-2">
      {outlookConnected ? (
        <>
          <button
            onClick={() => handleSync('microsoft')}
            className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" />
            Sync
          </button>
          <button
            onClick={() => handleDisconnect('microsoft')}
            className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md flex items-center gap-1"
          >
            <Trash2 className="w-4 h-4" />
            Remove
          </button>
        </>
      ) : (
        <button
          onClick={handleConnectOutlook}
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700"
        >
          Connect
        </button>
      )}
    </div>
  </div>
  {outlookConnected && lastSync && (
    <p className="text-xs text-gray-500 mt-2">Last synced: {lastSync}</p>
  )}
</div>

{/* ADD THE BANNER HERE */}
{waitingForOAuth && (
  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Check className="w-5 h-5 text-blue-600" />
        <div>
          <p className="font-medium text-blue-900">Calendar connected!</p>
          <p className="text-sm text-blue-700">Click 'Refresh Page' to update</p>
        </div>
      </div>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 flex items-center gap-2"
      >
        <RefreshCw className="w-4 h-4" />
        Refresh Page
      </button>
    </div>
  </div>
)}
</div>  {/* This closes the Calendar Integration section */}

{/* Availability Configuration */}
<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
  <div className="flex items-center gap-2 mb-4">
    <Clock className="w-5 h-5 text-indigo-600" />
    <h2 className="text-xl font-semibold text-gray-900">Availability Configuration</h2>
  </div>

          {/* Mode Selection */}
          <div className="space-y-3 mb-6">
            <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="availabilityMode"
                value="calendar"
                checked={availabilityMode === 'calendar'}
                onChange={(e) => {
                  setAvailabilityMode(e.target.value);
                  setHasChanges(true);
                }}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-900">Use Real Calendar Availability</div>
                <div className="text-sm text-gray-600 mt-1">
                  SurFox AI will reference your connected calendar's free/busy blocks and only propose times when you're available.
                </div>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="radio"
                name="availabilityMode"
                value="custom"
                checked={availabilityMode === 'custom'}
                onChange={(e) => {
                  setAvailabilityMode(e.target.value);
                  setHasChanges(true);
                }}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-900">Custom Availability Windows</div>
                <div className="text-sm text-gray-600 mt-1">
                  Define specific time windows when AI can propose meeting times, regardless of your calendar.
                </div>
              </div>
            </label>
          </div>

          {/* Custom Availability Windows */}
          {availabilityMode === 'custom' && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-medium text-gray-900 mb-4">Weekly Availability</h3>
              <div className="space-y-3">
                {/* The saved days come back from Postgres jsonb sorted by key length, then alphabet
                    (Friday, Monday, Sunday, Tuesday...), so put them in week order for display. */}
                {Object.entries(customAvailability)
                  .sort(([a], [b]) => {
                    const order = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                    const ia = order.indexOf(String(a).toLowerCase());
                    const ib = order.indexOf(String(b).toLowerCase());
                    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
                  })
                  .map(([day, settings]) => (
                  <div key={day} className="flex items-center gap-4">
                    <label className="flex items-center gap-2 w-32">
                      <input
                        type="checkbox"
                        checked={settings.enabled}
                        onChange={(e) =>
                          updateCustomAvailability(day, 'enabled', e.target.checked)
                        }
                        className="rounded"
                      />
                      <span className="text-sm font-medium text-gray-900 capitalize">
                        {day}
                      </span>
                    </label>
                    {settings.enabled && (
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={settings.start}
                          onChange={(e) =>
                            updateCustomAvailability(day, 'start', e.target.value)
                          }
                          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                        />
                        <span className="text-gray-500">to</span>
                        <input
                          type="time"
                          value={settings.end}
                          onChange={(e) =>
                            updateCustomAvailability(day, 'end', e.target.value)
                          }
                          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Call Duration & Buffer Time */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Call Settings</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Default Call Duration
              </label>
              <select
                value={callDuration}
                onChange={(e) => {
                  setCallDuration(Number(e.target.value));
                  setHasChanges(true);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-48"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>1 hour</option>
                <option value={90}>1.5 hours</option>
                <option value={120}>2 hours</option>
              </select>
              <p className="text-sm text-gray-600 mt-1">
                Used by SurFox when proposing time slots to leads.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Buffer Time
              </label>
              <select
                value={bufferTime}
                onChange={(e) => {
                  setBufferTime(Number(e.target.value));
                  setHasChanges(true);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-48"
              >
                <option value={0}>No buffer</option>
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
              </select>
              <p className="text-sm text-gray-600 mt-1">
                Padding added before and after scheduled events when generating time slots.
              </p>
            </div>
          </div>
        </div>

        {/* Scheduling Behavior */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-900">Scheduling Behavior</h2>
          </div>

          <div className="space-y-4">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={enforceBusinessHours}
                onChange={(e) => {
                  setEnforceBusinessHours(e.target.checked);
                  setHasChanges(true);
                }}
                className="mt-1 rounded"
              />
              <div>
                <div className="font-medium text-gray-900">Enforce Business Hours</div>
                <div className="text-sm text-gray-600">
                  Restrict AI to only propose times within your configured availability windows.
                </div>
              </div>
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Minimum Notice Window
              </label>
              <select
                value={minimumNotice}
                onChange={(e) => {
                  setMinimumNotice(Number(e.target.value));
                  setHasChanges(true);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm w-48"
              >
                <option value={0}>No minimum</option>
                <option value={1}>1 hour</option>
                <option value={2}>2 hours</option>
                <option value={4}>4 hours</option>
                <option value={24}>24 hours</option>
                <option value={48}>48 hours</option>
              </select>
              <p className="text-sm text-gray-600 mt-1">
                Prevents AI from booking calls within this time window from now.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {hasChanges && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">You have unsaved changes</p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
              >
                <X className="w-4 h-4 inline mr-1" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarSettings;